import { useState } from 'react';

export default function HomePage() {
  const [formData, setFormData] = useState({
    firstName: '',
    email: '',
    investorType: 'Private'
  });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus('submitting');

    try {
      const formElement = e.target as HTMLFormElement;
      const formDataToSubmit = new FormData(formElement);
      
      const response = await fetch('https://readdy.ai/api/form/d6g957or225iqdhs0me0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formDataToSubmit as any).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ firstName: '', email: '', investorType: 'Private' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    }
  };

  const opportunities = [
    {
      location: 'Manchester',
      type: 'Residential Conversion',
      description: 'Victorian warehouse conversion into 12 high-specification apartments in Northern Quarter.',
      targetReturn: '8.5',
      term: '18–24',
      security: 'First charge',
      minimum: '25,000',
      status: 'Open'
    },
    {
      location: 'Birmingham',
      type: 'New-Build Apartments',
      description: 'Ground-up development of 24 units near HS2 terminus with pre-sales secured.',
      targetReturn: '9.2',
      term: '24–30',
      security: 'Asset-backed',
      minimum: '50,000',
      status: 'Closing soon'
    },
    {
      location: 'Liverpool',
      type: 'PBSA (Student)',
      description: 'Purpose-built student accommodation, 86 beds, adjacent to university campus.',
      targetReturn: '7.8',
      term: '12–18',
      security: 'First charge',
      minimum: '20,000',
      status: 'Open'
    },
    {
      location: 'Leeds',
      type: 'Mixed-Use Refurbishment',
      description: 'Commercial ground floor with residential above, city centre location.',
      targetReturn: '8.9',
      term: '20–26',
      security: 'Asset-backed',
      minimum: '30,000',
      status: 'Open'
    },
    {
      location: 'Nottingham',
      type: 'Residential Portfolio',
      description: 'Acquisition and light refurbishment of 8 terraced properties for rental yield.',
      targetReturn: '7.5',
      term: '15–21',
      security: 'Portfolio charge',
      minimum: '15,000',
      status: 'Waitlist'
    },
    {
      location: 'Sheffield',
      type: 'Development Finance',
      description: 'Bridge financing for established developer, secured against land and planning consent.',
      targetReturn: '9.5',
      term: '12–15',
      security: 'First charge',
      minimum: '40,000',
      status: 'Open'
    }
  ];

  const faqs = [
    {
      question: 'Who are these opportunities suitable for?',
      answer: 'Our opportunities are designed for experienced investors, high-net-worth individuals, and sophisticated investors who understand property investment risks and can afford capital loss.'
    },
    {
      question: 'How is security structured?',
      answer: 'Security structures vary by opportunity but typically include first charges, asset-backed positions, or portfolio charges. Full details are provided in each investment pack.'
    },
    {
      question: 'What documentation do you provide?',
      answer: 'Each opportunity includes detailed investment memorandum, legal structure documentation, financial projections, risk analysis, and security documentation.'
    },
    {
      question: 'How do investor updates work?',
      answer: 'Investors receive quarterly progress reports, financial updates, and immediate notification of any material changes to the investment or project timeline.'
    },
    {
      question: 'What are the key risks?',
      answer: 'Property investments carry risks including capital loss, illiquidity, market volatility, development delays, and tenant default. Returns are not guaranteed and past performance is not indicative of future results.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#F6F3EE]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-[#F6F3EE]/95 backdrop-blur-sm z-50 border-b border-[#101214]/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 flex items-center">
          <div className="flex items-center gap-3">
            <img 
              src="https://public.readdy.ai/ai/img_res/66b9adfe-ad7e-4274-97ae-58298bb77d05.png" 
              alt="PIG Logo" 
              className="h-10 w-10 object-contain"
            />
            <div className="text-[#101214] font-semibold text-lg tracking-tight">
              PIG – Prop Invest Group
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 lg:pt-40 lg:pb-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/b46a77bebb98aec3c260db3276a78469.jpg"
            alt="UK property"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#101214]/75 via-[#101214]/65 to-[#101214]/80"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-4xl">
            <h1 className="text-5xl lg:text-7xl font-bold text-[#F6F3EE] leading-tight mb-6 tracking-tight">
              Curated UK property opportunities for growth and income.
            </h1>
            <p className="text-xl lg:text-2xl text-[#F6F3EE]/90 mb-10 leading-relaxed font-light">
              We source and present asset-backed investments with clear documentation and transparent underwriting.
            </p>
            
            <div className="flex flex-wrap gap-3 mb-12">
              <div className="px-5 py-2.5 bg-[#F6F3EE]/10 backdrop-blur-sm border border-[#F6F3EE]/20 rounded-full">
                <span className="text-sm font-medium text-[#F6F3EE]">UK-focused</span>
              </div>
              <div className="px-5 py-2.5 bg-[#F6F3EE]/10 backdrop-blur-sm border border-[#F6F3EE]/20 rounded-full">
                <span className="text-sm font-medium text-[#F6F3EE]">Asset-backed</span>
              </div>
              <div className="px-5 py-2.5 bg-[#F6F3EE]/10 backdrop-blur-sm border border-[#F6F3EE]/20 rounded-full">
                <span className="text-sm font-medium text-[#F6F3EE]">Deal-led</span>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Credibility Strip */}
      <section className="py-16 bg-[#101214]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center">
                <i className="ri-search-line text-3xl text-[#B79A5A]"></i>
              </div>
              <h3 className="text-[#F6F3EE] font-semibold text-base mb-2">Deal screening</h3>
              <p className="text-[#F6F3EE]/60 text-sm">Rigorous due diligence on every opportunity</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center">
                <i className="ri-file-text-line text-3xl text-[#B79A5A]"></i>
              </div>
              <h3 className="text-[#F6F3EE] font-semibold text-base mb-2">Clear documentation</h3>
              <p className="text-[#F6F3EE]/60 text-sm">Comprehensive investment packs</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center">
                <i className="ri-layout-grid-line text-3xl text-[#B79A5A]"></i>
              </div>
              <h3 className="text-[#F6F3EE] font-semibold text-base mb-2">Structured approach</h3>
              <p className="text-[#F6F3EE]/60 text-sm">Consistent process and methodology</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center">
                <i className="ri-notification-line text-3xl text-[#B79A5A]"></i>
              </div>
              <h3 className="text-[#F6F3EE] font-semibold text-base mb-2">Investor updates</h3>
              <p className="text-[#F6F3EE]/60 text-sm">Regular progress reporting</p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Opportunities */}
      <section className="py-24 bg-[#F6F3EE]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#101214] mb-4 tracking-tight">
              Current opportunities
            </h2>
            <p className="text-lg text-[#5D646B] max-w-2xl">
              Asset-backed property investments across the UK, each with detailed documentation and clear security structures.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 mb-8" data-product-shop>
            {opportunities.map((opp, index) => (
              <div key={index} className="bg-white rounded-lg border border-[#101214]/10 overflow-hidden hover:shadow-lg transition-shadow duration-300">
                <div className="h-48 w-full bg-gradient-to-br from-[#7E9B8E]/20 to-[#B79A5A]/10 relative overflow-hidden">
                  <img 
                    src={
                      index === 0
                        ? "https://readdy.ai/api/search-image?query=Victorian%20warehouse%20brick%20facade%20in%20Manchester%20Northern%20Quarter%20with%20arched%20windows%20and%20industrial%20character%2C%20warm%20afternoon%20light%20casting%20long%20shadows%20on%20textured%20red%20brick%20walls%2C%20urban%20regeneration%20property%20development%20with%20scaffolding%20and%20renovation%20details%2C%20clean%20minimal%20composition%20with%20neutral%20sky%20background&width=800&height=400&seq=opp-manchester-pig&orientation=landscape"
                        : index === 1
                        ? "https://readdy.ai/api/search-image?query=Modern%20new-build%20apartment%20block%20in%20Birmingham%20city%20centre%20with%20sleek%20glass%20curtain%20wall%20facade%20and%20contemporary%20architecture%2C%20crisp%20blue%20sky%20background%20with%20geometric%20building%20lines%20and%20balconies%2C%20high-rise%20residential%20development%20near%20transport%20hub%20with%20clean%20minimal%20composition&width=800&height=400&seq=opp-birmingham-pig&orientation=landscape"
                        : index === 2
                        ? "https://readdy.ai/api/search-image?query=Purpose-built%20student%20accommodation%20building%20in%20Liverpool%20with%20modern%20modular%20facade%20and%20colourful%20accent%20panels%2C%20vibrant%20urban%20campus%20setting%20with%20pedestrian%20plaza%20and%20bicycle%20racks%2C%20contemporary%20PBSA%20architecture%20with%20clean%20lines%20and%20neutral%20concrete%20tones&width=800&height=400&seq=opp-liverpool-pig&orientation=landscape"
                        : index === 3
                        ? "https://readdy.ai/api/search-image?query=Mixed-use%20development%20in%20Leeds%20city%20centre%20with%20ground%20floor%20retail%20units%20and%20residential%20apartments%20above%2C%20contemporary%20refurbishment%20of%20heritage%20building%20with%20stone%20facade%20and%20modern%20glazed%20extension%2C%20golden%20hour%20lighting%20on%20urban%20high%20street%20with%20minimal%20composition&width=800&height=400&seq=opp-leeds-pig&orientation=landscape"
                        : index === 4
                        ? "https://readdy.ai/api/search-image?query=Row%20of%20traditional%20Victorian%20terraced%20houses%20in%20Nottingham%20with%20bay%20windows%20and%20red%20brick%20facades%2C%20quiet%20residential%20street%20with%20mature%20trees%20and%20well-maintained%20front%20gardens%2C%20soft%20overcast%20light%20giving%20even%20tones%20across%20the%20property%20portfolio%20streetscape&width=800&height=400&seq=opp-nottingham-pig&orientation=landscape"
                        : "https://readdy.ai/api/search-image?query=Aerial%20view%20of%20Sheffield%20development%20site%20with%20cleared%20land%20and%20planning%20hoardings%2C%20surrounding%20urban%20landscape%20with%20mix%20of%20industrial%20and%20residential%20buildings%2C%20dramatic%20wide%20angle%20shot%20showing%20scale%20of%20development%20opportunity%20with%20overcast%20sky%20and%20neutral%20tones&width=800&height=400&seq=opp-sheffield-pig&orientation=landscape"
                    }
                    alt={`${opp.location} ${opp.type}`}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-4 right-4">
                    <span className={`px-3 py-1.5 text-xs font-semibold rounded-full whitespace-nowrap ${
                      opp.status === 'Open' ? 'bg-[#7E9B8E] text-white' :
                      opp.status === 'Closing soon' ? 'bg-[#B79A5A] text-[#101214]' :
                      'bg-[#5D646B] text-white'
                    }`}>
                      {opp.status}
                    </span>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="mb-3">
                    <h3 className="text-xl font-bold text-[#101214] mb-1">
                      {opp.location} – {opp.type}
                    </h3>
                    <p className="text-sm text-[#5D646B] leading-relaxed">
                      {opp.description}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-5 pt-4 border-t border-[#101214]/10">
                    <div>
                      <div className="text-xs text-[#5D646B] mb-1">Target return</div>
                      <div className="text-base font-bold text-[#101214]">{opp.targetReturn}% p.a.*</div>
                    </div>
                    <div>
                      <div className="text-xs text-[#5D646B] mb-1">Term</div>
                      <div className="text-base font-bold text-[#101214]">{opp.term} months</div>
                    </div>
                    <div>
                      <div className="text-xs text-[#5D646B] mb-1">Security</div>
                      <div className="text-base font-bold text-[#101214]">{opp.security}*</div>
                    </div>
                    <div>
                      <div className="text-xs text-[#5D646B] mb-1">Minimum</div>
                      <div className="text-base font-bold text-[#101214]">£{opp.minimum}</div>
                    </div>
                  </div>

                </div>
              </div>
            ))}
          </div>

          <p className="text-xs text-[#5D646B] italic">
            *Figures shown are illustrative placeholders. Capital at risk. Returns are not guaranteed.
          </p>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-16 text-center">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#101214] mb-4 tracking-tight">
              Our process
            </h2>
            <p className="text-lg text-[#5D646B] max-w-2xl mx-auto">
              A consistent, documentation-first approach to every opportunity.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="relative">
              <div className="w-16 h-16 bg-[#7E9B8E]/10 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl font-bold text-[#7E9B8E]">01</span>
              </div>
              <h3 className="text-xl font-bold text-[#101214] mb-2">Source</h3>
              <p className="text-sm text-[#5D646B] leading-relaxed">
                Identify opportunities through established networks and direct relationships with developers.
              </p>
            </div>

            <div className="relative">
              <div className="w-16 h-16 bg-[#7E9B8E]/10 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl font-bold text-[#7E9B8E]">02</span>
              </div>
              <h3 className="text-xl font-bold text-[#101214] mb-2">Screen</h3>
              <p className="text-sm text-[#5D646B] leading-relaxed">
                Conduct due diligence on asset quality, security structure, and sponsor track record.
              </p>
            </div>

            <div className="relative">
              <div className="w-16 h-16 bg-[#7E9B8E]/10 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl font-bold text-[#7E9B8E]">03</span>
              </div>
              <h3 className="text-xl font-bold text-[#101214] mb-2">Present</h3>
              <p className="text-sm text-[#5D646B] leading-relaxed">
                Prepare comprehensive documentation and present to qualified investors.
              </p>
            </div>

            <div className="relative">
              <div className="w-16 h-16 bg-[#7E9B8E]/10 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl font-bold text-[#7E9B8E]">04</span>
              </div>
              <h3 className="text-xl font-bold text-[#101214] mb-2">Update</h3>
              <p className="text-sm text-[#5D646B] leading-relaxed">
                Provide regular progress reports and maintain transparent communication throughout.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Track Record / Approach */}
      <section className="py-24 bg-[#F6F3EE]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl lg:text-5xl font-bold text-[#101214] mb-6 tracking-tight">
                Documentation-first approach
              </h2>
              <p className="text-lg text-[#5D646B] leading-relaxed mb-8">
                PIG presents opportunities with a documentation-first approach – clarity on the asset, the structure, and the assumptions.
              </p>
              <p className="text-base text-[#5D646B] leading-relaxed mb-8">
                Every investment pack includes detailed financial projections, legal structure documentation, security arrangements, risk analysis, and sponsor background. We believe transparency and thorough documentation are fundamental to informed investment decisions.
              </p>
              <div className="flex items-start gap-4 p-6 bg-white rounded-lg border border-[#101214]/10">
                <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                  <i className="ri-double-quotes-l text-2xl text-[#B79A5A]"></i>
                </div>
                <div>
                  <p className="text-base text-[#101214] italic mb-2">
                    "Clarity and process matter as much as yield."
                  </p>
                  <p className="text-sm text-[#5D646B] font-medium">– Investment Team</p>
                </div>
              </div>
            </div>

            <div className="relative h-[500px] rounded-lg overflow-hidden">
              <img 
                src="https://readdy.ai/api/search-image?query=Abstract%20architectural%20blueprint%20and%20technical%20drawings%20with%20clean%20geometric%20patterns%2C%20professional%20property%20development%20documentation%20with%20neutral%20tones%20and%20precise%20lines%2C%20sophisticated%20real%20estate%20planning%20imagery%20with%20minimal%20composition%20and%20structured%20layout&width=800&height=1000&seq=track-record-pig&orientation=portrait"
                alt="Documentation approach"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="mb-16 text-center">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#101214] mb-4 tracking-tight">
              Frequently asked questions
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-[#101214]/10 rounded-lg overflow-hidden bg-[#F6F3EE]/30">
                <div className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="text-lg font-bold text-[#101214] flex-1">
                      {faq.question}
                    </h3>
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                      <i className="ri-arrow-down-s-line text-xl text-[#5D646B]"></i>
                    </div>
                  </div>
                  <p className="text-sm text-[#5D646B] leading-relaxed mt-4">
                    {faq.answer}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Sign-up */}
      <section className="py-24 bg-[#101214]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#F6F3EE] mb-4 tracking-tight">
              Get new opportunities first.
            </h2>
            <p className="text-lg text-[#F6F3EE]/70">
              Monthly deal highlights, market notes and new listings. No spam.
            </p>
          </div>

          <form 
            id="newsletter-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="bg-white rounded-lg p-8 shadow-xl"
          >
            <div className="space-y-5 mb-6">
              <div>
                <label htmlFor="firstName" className="block text-sm font-semibold text-[#101214] mb-2">
                  First name
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-[#101214]/20 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#B79A5A] focus:border-transparent"
                  placeholder="Enter your first name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-[#101214] mb-2">
                  Email address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-[#101214]/20 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#B79A5A] focus:border-transparent"
                  placeholder="your.email@example.com"
                />
              </div>

              <div>
                <label htmlFor="investorType" className="block text-sm font-semibold text-[#101214] mb-2">
                  Investor type
                </label>
                <select
                  id="investorType"
                  name="investorType"
                  value={formData.investorType}
                  onChange={(e) => setFormData({ ...formData, investorType: e.target.value })}
                  className="w-full px-4 py-3 border border-[#101214]/20 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#B79A5A] focus:border-transparent"
                >
                  <option value="Private">Private</option>
                  <option value="Company">Company</option>
                  <option value="Introducer">Introducer</option>
                </select>
              </div>
            </div>

            <button
              type="submit"
              disabled={submitStatus === 'submitting'}
              className="w-full px-8 py-4 bg-[#B79A5A] text-[#101214] font-semibold text-base rounded-lg whitespace-nowrap hover:bg-[#B79A5A]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitStatus === 'submitting' ? 'Submitting...' : 'Join newsletter'}
            </button>

            {submitStatus === 'success' && (
              <div className="mt-4 p-4 bg-[#7E9B8E]/10 border border-[#7E9B8E] rounded-lg">
                <p className="text-sm text-[#7E9B8E] font-medium text-center">
                  Thank you for subscribing! Check your email for confirmation.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600 font-medium text-center">
                  Something went wrong. Please try again.
                </p>
              </div>
            )}

            <p className="text-xs text-[#5D646B] text-center mt-4">
              Capital at risk. Returns are not guaranteed.
            </p>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[#7E9B8E]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col items-center text-center mb-8">
            <div className="flex items-center gap-3 mb-4">
              <img 
                src="https://public.readdy.ai/ai/img_res/66b9adfe-ad7e-4274-97ae-58298bb77d05.png" 
                alt="PIG Logo" 
                className="h-10 w-10 object-contain"
              />
              <div className="text-[#F6F3EE] font-semibold text-lg">
                PIG – Prop Invest Group
              </div>
            </div>
            <p className="text-sm text-[#F6F3EE]/80 mb-6">
              Curated property deals. Clear documentation. Transparent process.
            </p>
          </div>

          <div className="border-t border-[#F6F3EE]/20 pt-8">
            <p className="text-xs text-[#F6F3EE]/70 text-center leading-relaxed mb-4 max-w-4xl mx-auto">
              Information on this site is for general purposes and does not constitute financial advice. Capital at risk. Returns are not guaranteed. Property investments are illiquid and may not be suitable for all investors. Past performance is not indicative of future results.
            </p>
            <p className="text-xs text-[#F6F3EE]/60 text-center">
              <a href="https://readdy.ai/?ref=logo" className="hover:text-[#F6F3EE] transition-colors whitespace-nowrap">
                Website Builder
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
